import isEmpty from 'lodash/isEmpty';
import uniqueId from 'lodash/uniqueId';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  addWidgetRegionJobThunk,
  deleteWidgetRegionJobThunk,
  getHRVacanciesListThunk,
  updateHRVacanciesCommentThunk,
} from '../../../../../redux/Dashboard/HRVacancies/HRVacancies.thunk';
import {
  getHRVacanciesList,
  getHRVacanciesLoading,
} from '../../../../../redux/Dashboard/HRVacancies/HRVacancies.slice';

import { useAppDispatch } from '../../../../../hooks/useAppDispatcher.hook';
import useAuthHook from '../../../../../hooks/useAuth.hook';
import { useModalManager } from '../../../../../hooks/ModalManager.hook';
import useRegionHook from '../../../../../hooks/useRegion.hook';

import {
  EditedHR,
  HRJob,
  WidgetRegionJobDTO,
} from '../../../../../redux/Dashboard/HRVacancies/HRVacancies.type';

import BaseWidgetWithLocalStoragePersistence from './BaseWidgetWithLocalStoragePersistence';
import { TableHeaderColumnType } from '../../../../../components/Table/TableHeader.component';
import TableComponent from '../../../../../components/Table/Table.component';
import NoResultComponent from '../../../../../components/NoResult.component';
import TableRowComponent from '../../../../../components/Table/TableRow.component';
import NotAssignComponent from '../../../../../components/NotAssign.component';
import UserBadgeComponent from '../../../../../components/UserBadge.component';
import HRVacancyEditModalComponent from '../Modal/HRVacancyEditModal.component';
import SelectComponent, {
  SelectOptionProps,
} from '../../../../../components/Input/Select.component';
import PenIcon from '../../../../../components/Icons/Pen.icon';
import IconButtonComponent from '../../../../../components/IconButton.component';
import VacancyCommentModalComponent from '../Modal/VacancyCommentModal.component';
import { checkUserRequiredRole } from '../../../../../utils/getUserRolesByToken';
import { UserRole } from '../../../../../types/userRole.types';
import TrashIcon from '../../../../../components/Icons/Trash.icon';
import HrWidgetJobRemoveAlert from '../../../../../components/Alert/HrWidget/HrWidgetRemoveJobAlert';
import { ALERT_ACTION_ENUM } from '../../../../../types/action.types';
import { WidgetKey } from '../../Dashboard.types';
import MessageIcon from '../../../../../components/Icons/Message.icon';
import { getLocalStorage, setLocalStorage } from '../../../../../utils/storage';
import PlusIcon from '../../../../../components/Icons/Plus.icon';
import { DragIcon } from '../../../../../components/Icons';
import { StyledSeparator } from '../../../ControlPanel/components/sharedStyledComponents';
import { HR } from '../../../../../redux/Dashboard/HRVacancies/HRVacancies.type';
import { storeHrWidgetThunk, getHrWidgetThunk } from '../../../../../redux/Dashboard/HRWidget/HRWidget.thunk';
import { HrWidgetDTO } from '../../../../../redux/Dashboard/HRWidget/HRWidget.type';
import {
  getHrVacanciesWidget,
  getInfoList, getInfoRegion,
} from '../../../../../redux/Dashboard/HRWidget/HRWidget.slice';

const StyledVacanciesWrapper = styled.div`
  display: grid;
  grid-template-columns: 30% 60% 10%;
  height: 100%;
  width: 100%;
`;

const StyledWidgetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  cursor: move;
  margin: 5px 10px;
`;

const StyledVacancy = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  > div {
    background-color: ${({ theme }) => theme.colors.BLUE.B100};
    padding: 3px 10px;
    border-radius: 3px;
    border: 1px solid ${({ theme }) => theme.colors.BORDER};
  }
`;

const StyledCommentWrapper = styled.div<{ hasComment: boolean; last: boolean }>`
  display: flex;
  align-items: center;
  flex: 1 1;
  font-weight: 400;
  color: currentColor !important;
  white-space: pre-wrap; 
  margin-bottom: 10px;
`;

const StyledActionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 10px;
`;

const StyledTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-grow: 1;
`;

const StyledIcon = styled.div`
  margin-right: 10px;
`;

const StyledUserBadge = styled.div`
  display: flex;
  align-items: center;
`;

const HRVacanciesWidgetComponent = () => {
  const { t } = useTranslation();
  const [region, setRegion] = useState<SelectOptionProps>();

  const list = useSelector(getHRVacanciesList);
  const loading = useSelector(getHRVacanciesLoading);
  const dispatch = useAppDispatch();
  const { user } = useAuthHook();
  const { openModal } = useModalManager();
  const {
    getSelectList: getRegionList,
    getSelectListOptionsByIds: getRegionListByIds,
  } = useRegionHook();

  const [mappedList, setMappedList] = useState<HR[]>([]);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragItem = useRef<any>(null);
  const dragOverItem = useRef<any>(null);
  const infoList = useSelector(getInfoList);
  const infoRegion = useSelector(getInfoRegion);

  useEffect(() => {
    dispatch(getHrWidgetThunk());
  }, [dispatch]);

  useEffect(() => {
    const parsedData = infoList;

    if (!Array.isArray(parsedData) || !parsedData.length) {
      setMappedList(list);
      return;
    }

    let newList: HR[] = [];

    parsedData.forEach((item: number) => {
      const findItem = list.find(i => i.user_id === item);
      if (findItem) {
        newList = [...newList, findItem];
      }
    })
    const restOfList = list.filter(item => !parsedData.includes(item.user_id))
    if (restOfList.length) {
      newList = [...newList, ...restOfList];
    }
    setMappedList(newList);
  }, [infoList, list]);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, item: any) => {
    dragItem.current = item;
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>, item: any) => {
    dragOverItem.current = item;
    setDragOverIndex(item.user_id);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (!dragItem.current || !dragOverItem.current) return;
    if (dragItem.current.user_id === dragOverItem.current.user_id) return;

    const newList = [...mappedList];
    const dragItemIndex = newList.findIndex(d => d.user_id === dragItem.current?.user_id);
    const dragOverItemIndex = newList.findIndex(d => d.user_id === dragOverItem.current?.user_id);

    if (dragItemIndex === -1 || dragOverItemIndex === -1) return;

    const [movedItem] = newList.splice(dragItemIndex, 1);
    newList.splice(dragOverItemIndex, 0, movedItem);

    dragItem.current = null;
    dragOverItem.current = null;
    setDragOverIndex(null);
    updateItems(newList);
  };

  const updateItems = (items: HR[]) => {
    const newItems = items.map((item: HR) => item.user_id);

    dispatch(storeHrWidgetThunk({
      info_list: newItems
    }));

    setMappedList(items);
  };

  const columnTitle: React.JSX.Element = (
    <StyledTitle style={{ display: 'flex', width: '100%'}}>
      <div style={{ width: '30%' }}>{t('Jobs')}</div>
      <div style={{ width: '60%' }}>{t('Comments')}</div>
      <div style={{ width: '10%' }}>&nbsp;</div>
    </StyledTitle>
  );

  const TABLE_HEADER_COLUMNS: TableHeaderColumnType[] = [
    {
      title: 'HR',
      dataProperty: 'hr',
      sortable: false,
      width: '22%',
    },
    {
      title: columnTitle,
      dataProperty: 'job',
      sortable: false,
      width: '70%',
    },
    {
      title: '',
      dataProperty: 'addVacancy',
      sortable: false,
      width: '8%',
    },
  ];

  useEffect(() => {
    if (!isEmpty(user.regions)) {
      const region = infoRegion;
      const savedRegion = (typeof region === 'string') ? JSON.parse(region) : region;
      setRegion(savedRegion || { value: user.regions[0]?.id, label: user.regions[0]?.name });

      if (user.regions[0]?.id) {
        dispatch(getHRVacanciesListThunk({ region_id: user.regions[0]?.id }));
      }
    }
  }, [user, infoRegion, dispatch]);

  useEffect(() => {
    if (region) dispatch(getHRVacanciesListThunk({ region_id: region.value }));
  }, [region]);

  const canChange = useMemo(() => {
    return checkUserRequiredRole(user.roles, [
      UserRole.SUPER_HR,
      UserRole.ADMIN,
      UserRole.DEV_ADMIN,
      UserRole.DEV_SUPER_HR,
      UserRole.HR,
      UserRole.DEV_HR,
    ]);
  }, [user]);

  const prepareRowColumns = (item: any) => {
    return [
      <StyledUserBadge data-set-width="22%">
        <StyledIcon>
          <DragIcon />
        </StyledIcon>
        <UserBadgeComponent key={item.user_id} info={item} />
      </StyledUserBadge>,
      item.jobs?.length ? (
          <StyledVacanciesWrapper data-set-width="70%">
            {item.jobs?.length ? (
              item.jobs?.map((job: HRJob, index: number) => (
                <React.Fragment key={job.id}>
                  <StyledVacancy>
                    <div>{job.job_name}</div>
                  </StyledVacancy>
                  <StyledCommentWrapper
                    hasComment={!!job.comment}
                    last={index === item.jobs?.length - 1}
                  >
                    {job.comment?.length ? (<div>{job.comment}</div>) : (<NotAssignComponent mode="badge" has={false} />)}
                  </StyledCommentWrapper>
                  {canChange && (
                    <StyledActionWrapper>
                      <IconButtonComponent
                        onClick={e => {
                          e.stopPropagation();
                          openModal(VacancyCommentModalComponent, {
                            sectionItem: item,
                            selectedJob: job,
                            updateComment,
                          });
                        }}
                      >
                        {job.comment ? <PenIcon /> : <MessageIcon />}
                      </IconButtonComponent>
                      <IconButtonComponent
                        onClick={e => {
                          e.stopPropagation();
                          confirmRemoveJob(job.id);
                        }}
                      >
                        <TrashIcon />
                      </IconButtonComponent>
                    </StyledActionWrapper>
                  )}
                </React.Fragment>
              ))
            ) : (
              <NotAssignComponent mode="badge" has={false} />
            )}
          </StyledVacanciesWrapper>
        ) : (
        <div data-set-width="70%">
          <NotAssignComponent mode="badge" has={false} />
        </div>
      ),
      <div data-set-width="8%">
        <IconButtonComponent
          key={item.user_id}
          onClick={() => (
            canChange &&
            openModal(HRVacancyEditModalComponent, {
              sectionItem: item,
              editSectionItem,
            })
          )}
        >
          <PlusIcon width={12} height={12} title='Add vacation' />
        </IconButtonComponent>
      </div>,
    ];
  };

  const editSectionItem = (sectionItem: EditedHR) => {
    dispatch(
      addWidgetRegionJobThunk({
        ...sectionItem,
        region_id: region!.value,
      } as any),
    ).then(() => {
      dispatch(getHRVacanciesListThunk({ region_id: region?.value }));
    });
  };

  const updateComment = (payload: {
    id: number;
    comment: Pick<WidgetRegionJobDTO, 'comment'>;
  }) => {
    dispatch(updateHRVacanciesCommentThunk(payload)).then(() => {
      dispatch(getHRVacanciesListThunk({ region_id: region?.value }));
    });
  };

  const confirmRemoveJob = (id: number) => {
    openModal(HrWidgetJobRemoveAlert, {
      handleConfirm: (action?: ALERT_ACTION_ENUM) => {
        if (action === ALERT_ACTION_ENUM.YES) {
          removeJob(id);
        }
      },
    });
  };

  const removeJob = (id: number) => {
    dispatch(deleteWidgetRegionJobThunk(id)).then(() => {
      dispatch(getHRVacanciesListThunk({ region_id: region?.value }));
    });
  };

  const regionsLoadOptions = (
    search: string,
    loadedOptions: string | SelectOptionProps[],
    { page, userRoleIds }: { page: number; userRoleIds: number[] },
  ) => {
    if (
      checkUserRequiredRole(user.roles, [UserRole.ADMIN, UserRole.DEV_ADMIN])
    ) {
      return getRegionList(search, loadedOptions, { page, userRoleIds });
    }

    return getRegionListByIds(
      user.regions.reduce((ids: number[], region: { id: number }) => {
        return [...ids, region.id];
      }, []),
    );
  };

  const handleSaveRegion = (value: SelectOptionProps) => {
    setRegion(value);

    dispatch(storeHrWidgetThunk({
      info_region: [JSON.stringify(value)]
    }));
  }

  const renderWidgetHeader = useMemo(
    () => (
      <StyledWidgetHeader>
        <p>{t('HR & Vacancies')}</p>
        <SelectComponent
          placeholder={t('Choose region')}
          value={region}
          isSearchable={false}
          isClearable={false}
          loadOptions={regionsLoadOptions}
          onChangeOption={handleSaveRegion}
          menuPlacement="bottom"
          useMenuPortalTarget
          valueContainerMaxWidth={200}
        />
      </StyledWidgetHeader>
    ),
    [region],
  );

  const renderContent = useMemo(() => {
    return mappedList.length > 0 ? (
      mappedList.map((item: HR) => {
        return (
          <React.Fragment key={item.user_id}>
            {dragOverIndex === item.user_id && <StyledSeparator key={`separator-${item.user_id}`} />}
            <div
              key={item.user_id}
              onDragStart={e => handleDragStart(e, item)}
              onDragEnter={e => handleDragEnter(e, item)}
              onDragOver={e => e.preventDefault()}
              onDragEnd={handleDrop}
              draggable
            >
              <TableRowComponent
                key={`table-row-${item.user_id}`}
                columns={prepareRowColumns(item)}
                withHint={false}
              />
            </div>
          </React.Fragment>
        );
      })
    ) : (
      <NoResultComponent />
    );
  }, [mappedList, dragOverIndex]);

  return (
    <BaseWidgetWithLocalStoragePersistence
      widgetKey={WidgetKey.HR_VACANCIES}
      header={renderWidgetHeader}
    >
      <TableComponent header={TABLE_HEADER_COLUMNS} loading={loading} type={'flex'}>
        {renderContent}
      </TableComponent>
    </BaseWidgetWithLocalStoragePersistence>
  );
};

export default HRVacanciesWidgetComponent;
